/**
 * Provenance DAG API — fetches nodes and edges for the project graph.
 */

import { apiClient } from "./client";

export interface DAGNodeMetadata {
  flow360_case_id?: string;
  base_case_id?: string;
  flow360_volume_mesh_id?: string;
  flow360_surface_mesh_id?: string;
  flow360_geometry_id?: string;
  status?: string;
  sweep_id?: string;
  sweep_params?: string[];
  [key: string]: unknown;
}

export interface DAGNode {
  id: string;
  path: string;
  kind: string;
  name: string;
  type: string;
  metadata?: DAGNodeMetadata;
  value_json?: Record<string, unknown>;
  [key: string]: unknown;
}

export interface DAGEdge {
  id: string;
  parent_id: string;
  child_id: string;
  relationship?: string;
  operation?: string;
  metadata?: Record<string, unknown>;
  created_at?: string;
}

export interface DAGData {
  nodes: DAGNode[];
  edges: DAGEdge[];
}

export interface FetchLineageOptions {
  scope?: "all" | "runs";
  includePlots?: boolean;
}

export async function fetchLineage(
  projectId: string,
  branch = "main",
  options: FetchLineageOptions = {},
): Promise<DAGData> {
  const branchParam = encodeURIComponent(branch);
  const { scope, includePlots = true } = options;
  const scopeParam = scope ? `&scope=${scope}` : "";
  const plotsParam = includePlots ? "&include_plots=true" : "";
  // Single round-trip. `/nodes/dag` resolves the run subgraph once; the old
  // pair of calls made the recursive CTE run twice per DAG load.
  const combined = await apiClient<{
    nodes: Record<string, unknown>[];
    edges: Record<string, unknown>[];
  }>(`/nodes/dag?project_id=${projectId}&branch=${branchParam}${plotsParam}${scopeParam}`);
  const rawNodes = combined.nodes;
  const rawEdges = combined.edges;

  const nodes: DAGNode[] = rawNodes.map((n) => {
    const pathStr = String(n.path ?? "");
    const segments = pathStr.split(".");
    return {
      ...n,
      id: String(n.id),
      path: pathStr,
      kind: String(n.kind ?? "unknown"),
      name: String(n.name ?? "") || segments[segments.length - 1] || pathStr,
      type: String(n.kind ?? "unknown"),
    };
  });

  const edges: DAGEdge[] = rawEdges.map((e, i) => ({
    id: String(e.id ?? `edge-${i}`),
    parent_id: String(e.parent_id),
    child_id: String(e.child_id),
    relationship: e.relationship ? String(e.relationship) : undefined,
    operation: e.operation ? String(e.operation) : undefined,
    metadata: e.metadata && typeof e.metadata === "object" ? (e.metadata as Record<string, unknown>) : undefined,
    created_at: e.created_at ? String(e.created_at) : undefined,
  }));

  return { nodes, edges };
}

export async function deleteNode(nodeId: string): Promise<void> {
  await apiClient(`/nodes/${nodeId}`, { method: "DELETE" });
}

export async function restoreNode(nodeId: string): Promise<void> {
  await apiClient(`/nodes/${nodeId}/restore`, { method: "POST" });
}
